﻿import { vec2 } from "gl-matrix";
import { ElementEvent, ElementPointerEvent, ElementTouchEvent } from "../events/element-event";

export enum EPointerButton {
    NONE,
    LEFT,
    RIGHT,
    WHEEL,
    BACK,
    FORWARD
}

export class Input {

    private _keyMap: { [name: string]: number } = {};
    private _keysPressed: string[] = [];
    private _buttonsPressed: EPointerButton[] = [];
    private _buttonMap: { [button: number]: number } = {};

    private _direction = vec2.create();
    private _start = vec2.fromValues(-1, -1);
    zoom: number = 0;
    private _abortController: AbortController;
    root: HTMLElement;
    private _rootBounds: DOMRect;
    ndc = vec2.create();
    position = vec2.create();
    startNdc = vec2.create();
    deltaNdc = vec2.create();


    initialize(root: HTMLElement): void {
        this.root = root;
        
        this._abortController = new AbortController();
        document.body.addEventListener("keydown", (ev) => { this.keyDown(ev) }, { signal: this._abortController.signal })
        document.body.addEventListener("keyup", (ev) => { this.keyUp(ev) }, { signal: this._abortController.signal })
        root.addEventListener("pointermove", (ev) => { this.pointerMove(new ElementPointerEvent(ev, root)) }, { signal: this._abortController.signal })
        root.addEventListener("pointerdown", (ev) => { this.pointerDown(new ElementPointerEvent(ev, root)) }, { signal: this._abortController.signal })
        root.addEventListener("pointerup", (ev) => { this.pointerUp(new ElementPointerEvent(ev, root)) }, { signal: this._abortController.signal })

        root.addEventListener("touchmove", (ev) => { this.pointerMove(new ElementTouchEvent(ev, root)) }, { signal: this._abortController.signal })
        root.addEventListener("touchstart", (ev) => { this.pointerDown(new ElementTouchEvent(ev, root)) }, { signal: this._abortController.signal })
        root.addEventListener("touchend", (ev) => { this.pointerUp(new ElementTouchEvent(ev, root)) }, { signal: this._abortController.signal })

        root.addEventListener("wheel", (ev) => { this.zoomEvent(ev.deltaY) }, { signal: this._abortController.signal });
        this._rootBounds = root.getBoundingClientRect();

    }

    get center() {
        return vec2.fromValues(
            this.centerX,
            this.centerY
        )
    }

    get centerX() {
        return (this._rootBounds.width) * 0.5
    }

    get centerY() {
        return (this._rootBounds.height) * 0.5
    }

    private pointerDown(ev: ElementEvent): void {
        if (!ev.anyButton) return;
        const btn = ev.buttonPressed;

        vec2.set(
            this._start,
            ev.clientX,
            ev.clientY
        );

        vec2.set(
            this.startNdc,
            ev.ndc[0],
            ev.ndc[1]
        )

        this._buttonMap[ev.buttonPressed] = -1;
    }
    private pointerUp(ev: ElementEvent): void {
        for (var prop in this._buttonMap) {
            this._buttonMap[prop] = 1;
            this._buttonsPressed.push(this._buttonMap[prop]);
        }
    }
    private pointerMove(ev: ElementEvent): void {

        vec2.sub(this.deltaNdc, ev.ndc, this.ndc);// ev.ndc.clone().sub(this.ndc);
        this.ndc = ev.ndc;
        this._rootBounds = this.root.getBoundingClientRect();

        vec2.normalize(
            this._direction,
            this.ndc
        )

        vec2.set(
            this.position,
            ev.clientX,
            ev.clientY
        )

    }

    private zoomEvent(value: number): void {
        this.zoom = value < 0 ? -1 : 1;
    }

    private keyDown(ev: KeyboardEvent): void {
        const lower = ev.key.toLowerCase();
        this._keyMap[lower] = this._keyMap[lower] === 0 ? -2 : -1;

    }

    private keyUp(ev: KeyboardEvent): void {
        const key = ev.key.toLowerCase();
        this._keyMap[key] = 1;
        this._keysPressed.push(key);
    }

    isKeyDown(key: string): boolean {
        return this._keyMap[key] < 0;
    }

    anyKeyDown(): boolean {
        for (var prop in this._keyMap) {
            if (this._keyMap[prop] < 0) return true;
        }

        return false;
    }

    get direction(): vec2 {
        return this._direction;
    }

    wasKeyClicked(key: string): boolean {
        const val = this._keyMap[key];
        return val > 0;
    }

    hasPointerMoved(): boolean {
        return this._direction.length > 0.001;
    }

    isButtonDown(btn: EPointerButton): boolean {
        return this._buttonMap[btn] < 0;
    }

    wasButtonClicked(btn: EPointerButton): boolean {
        return this._buttonMap[btn] > 0;
    }
    wasKeyFirstPressedThisFrame(key: string): boolean {
        return this._keyMap[key] === -2;
    }
    wasButtonFirstPressedThisFrame(btn: EPointerButton): boolean {
        return this._buttonMap[btn] === -2;
    }

    private resetKeysPressed(): void {
        for (var i = 0; i < this._keysPressed.length; i++) {
            const key = this._keysPressed[i];

            if (this._keyMap[key] === -2) {
                this._keyMap[key] = -1;
            }
            else this._keyMap[key] = 0;
        }

        this._keysPressed = [];
    }

    private resetButtonsPressed(): void {
        for (var i = 0; i < this._buttonsPressed.length; i++) {
            const key = this._buttonsPressed[i];
            if (this._buttonMap[key] === -2) {
                this._buttonMap[key] = -1;
            }
            else this._buttonMap[key] = 0;
        }

        this._buttonsPressed = [];
    }

    clearFrameInputs(): void {
        this.resetKeysPressed();
        this.resetButtonsPressed();

        this.zoom = 0;
        this._direction[0] = 0;
        this._direction[1] = 0;
        vec2.set(
            this.deltaNdc,
            0, 0
        )

    }

}